import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { createEmployee, fetchDepartments, fetchEmployees, fetchHrSettings, fetchLinkableUsers } from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, StatusPill, inputClass, useHr } from "./hrUi";

const empty = {
  userId: "",
  fullName: "",
  workEmail: "",
  phoneNumber: "",
  hiringDate: "",
  departmentId: "",
  jobTitleId: "",
  directManagerEmployeeId: "",
  employmentTypeId: "",
  employmentStatus: "Active",
  profilePictureUrl: "",
  administrativeNotes: "",
};

export default function EmployeesPage() {
  return (
    <HrGuard anyOf={["HR.ViewEmployees", "HR.ManageEmployees", "HR.ViewDepartmentEmployees"]}>
      <EmployeesBody />
    </HrGuard>
  );
}

function EmployeesBody() {
  const navigate = useNavigate();
  const hr = useHr();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["hrEmployees", search, departmentId, status],
    queryFn: () => fetchEmployees({ search, departmentId, status, pageSize: 50 }),
  });
  const departments = useQuery({ queryKey: ["hrDepartments"], queryFn: fetchDepartments });
  const settings = useQuery({ queryKey: ["hrSettings"], queryFn: fetchHrSettings, enabled: open });
  const users = useQuery({ queryKey: ["hrLinkableUsers"], queryFn: fetchLinkableUsers, enabled: open && hr.can("HR.ManageEmployees") });

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const created = await createEmployee({
        ...form,
        userId: Number(form.userId),
        departmentId: form.departmentId ? Number(form.departmentId) : null,
        jobTitleId: form.jobTitleId ? Number(form.jobTitleId) : null,
        directManagerEmployeeId: form.directManagerEmployeeId ? Number(form.directManagerEmployeeId) : null,
        employmentTypeId: form.employmentTypeId ? Number(form.employmentTypeId) : null,
      });
      toast.success("تم إنشاء ملف الموظف");
      setOpen(false);
      setForm(empty);
      queryClient.invalidateQueries({ queryKey: ["hrEmployees"] });
      if (created?.id) navigate(`/hr/employees/${created.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const rows = employees.data?.items || [];

  return (
    <HrFrame
      title="الموظفون"
      subtitle="كل موظف مرتبط بحساب موجود. تعطيل الموظف لا يحذف سجلاته."
      action={hr.can("HR.ManageEmployees") && (
        <button type="button" className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white" onClick={() => setOpen(true)}>
          موظف جديد
        </button>
      )}
    >
      <div className="mb-4 grid gap-2 md:grid-cols-3">
        <input className={inputClass} placeholder="بحث بالاسم أو الكود أو الإيميل" value={search} onChange={(event) => setSearch(event.target.value)} />
        <select className={inputClass} value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
          <option value="">كل الأقسام</option>
          {(departments.data || []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
        </select>
        <select className={inputClass} value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">كل الحالات</option>
          <option value="Active">على رأس العمل</option>
          <option value="Suspended">موقوف</option>
          <option value="Resigned">مستقيل</option>
        </select>
      </div>

      {employees.isLoading && <p className="text-sm text-slate-500">جار التحميل…</p>}
      {employees.error && <p className="text-sm text-rose-600">{employees.error.message}</p>}
      {!employees.isLoading && rows.length === 0 && <Empty text="لا يوجد موظفون في نطاق صلاحيتك." />}

      <div className="grid gap-3">
        {rows.map((employee) => (
          <button
            key={employee.id}
            type="button"
            onClick={() => navigate(`/hr/employees/${employee.id}`)}
            className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-right shadow-sm"
          >
            <div className="min-w-0">
              <p className="truncate font-bold text-slate-900">{employee.fullName}</p>
              <p className="truncate text-sm text-slate-500">{employee.employeeCode} · {employee.departmentName || "بدون قسم"} · {employee.jobTitle || "بدون مسمى"}</p>
            </div>
            <StatusPill value={employee.employmentStatus} />
          </button>
        ))}
      </div>

      {open && (
        <form onSubmit={submit} className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4">
          <div className="mx-auto max-w-lg rounded-2xl bg-white p-4">
            <h2 className="mb-3 text-lg font-bold">ربط حساب موجود بملف موظف</h2>
            <div className="grid gap-3">
              <Field label="الحساب">
                <select className={inputClass} required value={form.userId} onChange={(event) => set("userId", event.target.value)}>
                  <option value="">اختر حساباً</option>
                  {(users.data || []).map((user) => <option key={user.id} value={user.id}>{user.email}</option>)}
                </select>
              </Field>
              <Field label="الاسم"><input className={inputClass} required value={form.fullName} onChange={(event) => set("fullName", event.target.value)} /></Field>
              <Field label="إيميل العمل"><input className={inputClass} value={form.workEmail} onChange={(event) => set("workEmail", event.target.value)} /></Field>
              <Field label="الهاتف"><input className={inputClass} value={form.phoneNumber} onChange={(event) => set("phoneNumber", event.target.value)} /></Field>
              <Field label="تاريخ التعيين"><input className={inputClass} type="date" required value={form.hiringDate} onChange={(event) => set("hiringDate", event.target.value)} /></Field>
              <Field label="القسم">
                <select className={inputClass} value={form.departmentId} onChange={(event) => set("departmentId", event.target.value)}>
                  <option value="">بدون</option>
                  {(departments.data || []).filter((item) => item.isActive).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </Field>
              <Field label="المسمى">
                <select className={inputClass} value={form.jobTitleId} onChange={(event) => set("jobTitleId", event.target.value)}>
                  <option value="">بدون</option>
                  {(settings.data?.jobTitles || []).filter((item) => item.isActive).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </Field>
              <Field label="نوع التوظيف">
                <select className={inputClass} value={form.employmentTypeId} onChange={(event) => set("employmentTypeId", event.target.value)}>
                  <option value="">بدون</option>
                  {(settings.data?.employmentTypes || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </Field>
              <Field label="صورة (رابط)"><input className={inputClass} value={form.profilePictureUrl} onChange={(event) => set("profilePictureUrl", event.target.value)} /></Field>
            </div>
            <div className="mt-4 flex gap-2">
              <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white" disabled={saving}>{saving ? "جار الحفظ" : "حفظ"}</button>
              <button type="button" className="rounded-xl px-4 py-2 text-sm" onClick={() => setOpen(false)}>إلغاء</button>
            </div>
          </div>
        </form>
      )}
    </HrFrame>
  );
}
